'use client'

// MoodRig — the single driver that turns the live sim environment into
// the world's LOOK. Runs one useFrame:
//   env (gameStore) → computeMoodTargets → smoothed → mood uniforms (TSL)
//   + wind strength (danger braces the world)
//   + scene.environmentIntensity (IBL fades out at night)
// No materials read the store; they read the shared mood uniforms.

import { useFrame, useThree } from '@react-three/fiber'
import { useEffect, useRef } from 'react'
import useGameStore from '../../../stores/gameStore'
import { mood, moodState, computeMoodTargets } from '../../../lib/world/mood'
import { wind } from '../effects/wind/Wind'

const BASE_WIND = 0.5
const DAY_IBL = 0.45
// Lifted a touch so the ambient image-light keeps the night readable rather
// than crushing it to near-black.
const NIGHT_IBL = 0.13
const MOOD_KEYS = ['nightness', 'wetness', 'overcast', 'danger', 'vitality', 'warmth', 'abundance']

export function MoodRig() {
  const { scene } = useThree()
  const target = useRef(null)
  const lastEnv = useRef(null)
  // Dev-only override that OUTLASTS the sim. setDayPhase below writes into
  // the store and a live world_delta overwrites it on the next tick, which
  // makes it useless for an art pass against the real world (you get about
  // a second of night). This is applied after the smoothing instead, so it
  // holds until you let go. null = not pinned.
  const pinned = useRef(null)

  // Dev-only console hook — art-pass day/night with NO sim running:
  //   setDayPhase(0)  → midnight   (0.25 dawn · 0.5 noon · 0.75 dusk)
  //   forceNight() / forceDay() — shortcuts
  // Writes dayPhase into the store's environment — the same field the sim
  // broadcasts — so the sky, mood uniforms, ground pools and moonlight all
  // follow coherently. A live sim's world_delta will overwrite it; this is
  // for sim-less sessions.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return
    const setPhase = (p) => {
      const s = useGameStore.getState()
      s.setEnvironment({ ...(s.environment || {}), dayPhase: p })
    }
    window.setDayPhase = setPhase
    window.forceNight = () => setPhase(0)
    window.forceDay = () => setPhase(0.5)
    // pinNight(1) holds full night against a live sim, pinNight(0) holds
    // full day, pinNight() lets the world have it back. For judging window
    // lights and anything else that only exists after dark.
    window.pinNight = (v) => {
      pinned.current = v == null ? null : Math.max(0, Math.min(1, Number(v) || 0))
      return pinned.current == null ? 'released' : `nightness pinned at ${pinned.current}`
    }
    return () => {
      delete window.setDayPhase
      delete window.forceNight
      delete window.forceDay
      delete window.pinNight
    }
  }, [])

  useFrame((_, dt) => {
    // Dev-only spike logger: when a frame blows its budget, print the mood
    // state it happened at, so transition hitches (dusk, rain starting)
    // stop being guesswork. 80ms = a very visible stutter at 60fps.
    if (process.env.NODE_ENV !== 'production' && dt > 0.08) {
      const note = dt > 1 ? ' (probably a tab switch or load, not a render hitch)' : ''
      console.log(
        `[spike] ${(dt * 1000) | 0}ms frame at nightness=${moodState.nightness.toFixed(2)} wetness=${moodState.wetness.toFixed(2)} overcast=${moodState.overcast.toFixed(2)}${note}`
      )
    }
    const env = useGameStore.getState().environment
    // Recompute only when a new environment object arrives (5Hz), not per
    // frame: this was allocating a fresh targets object at 60fps from
    // data that cannot change between broadcasts.
    if (env && env !== lastEnv.current) {
      lastEnv.current = env
      target.current = computeMoodTargets(env)
    }
    const t = target.current
    if (!t) return

    // Smooth toward targets — slow enough that a governance flip feels
    // like weather arriving, not a light switch.
    const a = Math.min(1, dt * 0.5)
    for (const k of MOOD_KEYS) {
      moodState[k] += (t[k] - moodState[k]) * a
      if (mood[k]) mood[k].value = moodState[k]
    }

    // The raw day phase, for anything that needs WHERE the world is in
    // its day rather than how dark it is: the sun's arc reads this. Not
    // put through the smoothing above on purpose (see moodState.dayPhase).
    if (typeof env?.dayPhase === 'number') moodState.dayPhase = env.dayPhase
    // Raw for the same reason, and because it is a boolean: there is no
    // meaningful value between a day that turns over and one that has
    // stopped. The SKY still eases, because nightness is smoothed above
    // and the held sun just stops feeding it new targets.
    moodState.sunStuck = !!env?.sunStuck
    // Raw for the same wrap-around reason as dayPhase, and it must land
    // before anything draws this frame: the sun's POSITION reads it.
    if (typeof env?.sunPhase === 'number') moodState.sunPhase = env.sunPhase
    else if (typeof env?.dayPhase === 'number') moodState.sunPhase = env.dayPhase

    // The dev pin, applied last so nothing downstream can argue with it.
    if (process.env.NODE_ENV !== 'production' && pinned.current != null) {
      moodState.nightness = pinned.current
      mood.nightness.value = pinned.current
    }

    // Danger + storms raise the wind (shared wind uniform — trees, debris).
    wind.strength.value = BASE_WIND + moodState.danger * 2.0 + moodState.wetness * 0.8

    // Image-based light fades at night so the dark actually lands.
    scene.environmentIntensity = DAY_IBL * (1 - moodState.nightness) + NIGHT_IBL * moodState.nightness
  })

  return null
}

export default MoodRig
